// ORCA360 - Custom Audio Recorder Hook
// Uses native MediaRecorder API for in-browser recording
// Format: audio/webm;codecs=opus (universal browser support)
// Features: chunked recording, audio level monitoring, wake lock, haptic feedback

import { useState, useRef, useCallback, useEffect } from 'react';

export type RecorderState =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'stopped'
  | 'error';

export interface RecorderError {
  code: 'NOT_ALLOWED' | 'NOT_FOUND' | 'NOT_SUPPORTED' | 'UNKNOWN';
  message: string;
}

export interface UseAudioRecorderOptions {
  /** Chunk interval in milliseconds (default: 30000 = 30s) */
  chunkIntervalMs?: number;
  /** Called when a new chunk is available during recording */
  onChunkAvailable?: (chunk: Blob, index: number) => void;
  /** Called when recording stops with the complete blob */
  onRecordingComplete?: (blob: Blob, durationSeconds: number) => void;
  /** Called on error */
  onError?: (error: RecorderError) => void;
}

export interface UseAudioRecorderReturn {
  state: RecorderState;
  error: RecorderError | null;
  /** Current audio level 0-1 */
  audioLevel: number;
  /** Duration in seconds */
  durationSeconds: number;
  /** Number of chunks recorded so far */
  chunkCount: number;
  /** Detected MIME type */
  mimeType: string | null;
  /** Start recording */
  start: () => Promise<void>;
  /** Pause recording */
  pause: () => void;
  /** Resume recording */
  resume: () => void;
  /** Stop recording */
  stop: () => void;
}

// MIME type fallback chain
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function detectMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return null;
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}): UseAudioRecorderReturn {
  const {
    chunkIntervalMs = 30000,
    onChunkAvailable,
    onRecordingComplete,
    onError,
  } = options;

  const [state, setState] = useState<RecorderState>('idle');
  const [error, setError] = useState<RecorderError | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [chunkCount, setChunkCount] = useState(0);
  const [mimeType, setMimeType] = useState<string | null>(null);

  // Refs for cleanup
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number>(0);
  const chunkIndexRef = useRef<number>(0);
  const chunksRef = useRef<Blob[]>([]);
  const onChunkAvailableRef = useRef(onChunkAvailable);
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  const onErrorRef = useRef(onError);

  // Keep callback refs up to date
  useEffect(() => {
    onChunkAvailableRef.current = onChunkAvailable;
    onRecordingCompleteRef.current = onRecordingComplete;
    onErrorRef.current = onError;
  }, [onChunkAvailable, onRecordingComplete, onError]);

  // Audio level monitoring loop
  const startLevelMonitoring = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        // Calculate RMS-like level from frequency data
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = dataArray[i] ?? 0;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        // Normalize to 0-1 range (byte data goes 0-255)
        const level = Math.min(rms / 128, 1);
        setAudioLevel(level);
        animFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // AudioContext not available — not critical
    }
  }, []);

  const stopLevelMonitoring = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Wake lock management
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch {
      // Wake lock not available or denied — non-critical
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  // Timer management
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    pausedDurationRef.current = 0;
    setDurationSeconds(0);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
      setDurationSeconds(Math.floor(elapsed / 1000));
    }, 250);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Haptic feedback
  const vibrate = useCallback((pattern: number | number[]) => {
    try {
      if ('vibrate' in navigator) {
        navigator.vibrate(pattern);
      }
    } catch {
      // Not available
    }
  }, []);

  // Cleanup all resources
  const cleanup = useCallback(() => {
    stopLevelMonitoring();
    stopTimer();
    releaseWakeLock();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, [stopLevelMonitoring, stopTimer, releaseWakeLock]);

  // Start recording
  const start = useCallback(async () => {
    if (state === 'recording' || state === 'paused') return;

    setState('requesting');
    setError(null);
    setChunkCount(0);
    chunkIndexRef.current = 0;
    chunksRef.current = [];

    const detectedMime = detectMimeType();
    if (!detectedMime) {
      const err: RecorderError = {
        code: 'NOT_SUPPORTED',
        message: 'Your browser does not support audio recording. Please use Chrome, Firefox, or Edge.',
      };
      setError(err);
      setState('error');
      onErrorRef.current?.(err);
      return;
    }
    setMimeType(detectedMime);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
        },
      });

      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: detectedMime,
      });

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
          const idx = chunkIndexRef.current;
          chunkIndexRef.current += 1;
          setChunkCount(prev => prev + 1);
          onChunkAvailableRef.current?.(event.data, idx);
        }
      };

      recorder.onstop = () => {
        const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
        const totalDuration = Math.floor(elapsed / 1000);

        // Build complete blob from all chunks
        const completeBlob = new Blob(chunksRef.current, { type: detectedMime });

        // Stop stream tracks AFTER final data has been flushed to avoid
        // losing the last audio chunk (MediaRecorder needs the stream alive
        // until onstop fires).
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        setState('stopped');
        stopTimer();
        stopLevelMonitoring();
        releaseWakeLock();
        vibrate(200);

        onRecordingCompleteRef.current?.(completeBlob, totalDuration);
      };

      recorder.onerror = () => {
        const err: RecorderError = {
          code: 'UNKNOWN',
          message: 'Recording failed unexpectedly. Please try again.',
        };
        setError(err);
        setState('error');
        cleanup();
        onErrorRef.current?.(err);
      };

      mediaRecorderRef.current = recorder;

      // Start recording with chunk interval
      recorder.start(chunkIntervalMs);
      setState('recording');
      startTimer();
      startLevelMonitoring(stream);
      await acquireWakeLock();
      vibrate(100);
    } catch (err: unknown) {
      const domError = err as DOMException;
      let recorderError: RecorderError;

      if (domError.name === 'NotAllowedError') {
        recorderError = {
          code: 'NOT_ALLOWED',
          message:
            'Microphone access was denied. Please allow microphone access in your browser settings and try again.',
        };
      } else if (domError.name === 'NotFoundError') {
        recorderError = {
          code: 'NOT_FOUND',
          message:
            'No microphone found. Please connect a microphone and try again.',
        };
      } else {
        recorderError = {
          code: 'UNKNOWN',
          message: `Failed to start recording: ${domError.message || 'Unknown error'}`,
        };
      }

      setError(recorderError);
      setState('error');
      cleanup();
      onErrorRef.current?.(recorderError);
    }
  }, [state, chunkIntervalMs, startTimer, startLevelMonitoring, acquireWakeLock, vibrate, cleanup, stopTimer, stopLevelMonitoring, releaseWakeLock]);

  // Pause recording
  const pause = useCallback(() => {
    if (state !== 'recording' || !mediaRecorderRef.current) return;

    mediaRecorderRef.current.pause();
    pauseStartRef.current = Date.now();
    setState('paused');
    stopLevelMonitoring();
    vibrate(50);
  }, [state, stopLevelMonitoring, vibrate]);

  // Resume recording
  const resume = useCallback(() => {
    if (state !== 'paused' || !mediaRecorderRef.current || !streamRef.current) return;

    pausedDurationRef.current += Date.now() - pauseStartRef.current;
    mediaRecorderRef.current.resume();
    setState('recording');
    startLevelMonitoring(streamRef.current);
    vibrate(50);
  }, [state, startLevelMonitoring, vibrate]);

  // Stop recording
  const stop = useCallback(() => {
    if (!mediaRecorderRef.current || (state !== 'recording' && state !== 'paused')) return;

    setState('stopping');
    // If paused, account for paused time
    if (state === 'paused') {
      pausedDurationRef.current += Date.now() - pauseStartRef.current;
    }
    mediaRecorderRef.current.stop();
    // Stream tracks are stopped in the onstop handler after final data is flushed
  }, [state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    error,
    audioLevel,
    durationSeconds,
    chunkCount,
    mimeType,
    start,
    pause,
    resume,
    stop,
  };
}
